// ABOUTME: Changes an applicant stage from the candidate job list.
// ABOUTME: Reconciles the owning view with the confirmed stage.
import React from "react";
import { createStyles, Theme, makeStyles } from "@material-ui/core/styles";
import { useQueryClient } from "react-query";
import useApplicantViewContext from "../useApplicantViewContext";
import {
    IJobViewStages,
    IJobViewStagesActionDefinition
} from "Pages/Job/JobView/useJobContext/JobViewTypes";
import { useJobContextSafe } from "Pages/Job/JobView/useJobContext";
import { pipelineAllActionsFields } from "Pages/Pipeline/components/helperPipeline";
import { useApiCache } from "customHooks/useApi";
import useRefreshKanban from "Pages/Job/JobView/JobKanban/useRefreshKanban";
import {
    normalizeApplicantId,
    refreshApplicantProfileAfterStageChange,
    refreshApplicantStageView
} from "Pages/Job/JobView/JobKanban/applicantStageReadConsistency";
import {
    ApplicantStageMutationState,
    startApplicantStageChange
} from "Pages/Job/JobView/JobKanban/applicantStageMutationGeneration";
import ChangeStageButton from "./ChangeStageButton";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: "flex",
            flexDirection: "column",
            width: "100%"
        }
    })
);

interface IProps {
    JobData: Compleo.IObject;
    t: any;
    index: number;
}

const ChangeStageButtonOutsideJobContainer = (props: IProps) => {
    const { t, index, JobData } = props;
    const classes = useStyles();
    const [data] = useApplicantViewContext();
    const { language } = data;
    const updateAllInfo = useRefreshKanban();
    const jobContext = useJobContextSafe();
    const queryClient = useQueryClient();
    const hasJobContext = jobContext !== null;
    const applicantStageMutationGenerations = React.useRef<
        Map<string, ApplicantStageMutationState>
    >(new Map());
    React.useEffect(
        () => () => applicantStageMutationGenerations.current.clear(),
        []
    );
    const [stages, setStages] = React.useState<IJobViewStages[]>([]);
    const keysToInvalidate: string[] = [
        "/applicant/list",
        "/job/applicantlistjobview",
        "/applicant/applicanthistorylist",
        "/reports/jobfunnel"
    ];

    const startJobStageChange = (applicantId: string) =>
        startApplicantStageChange(
            jobContext?.[0].applicantStageMutationGenerations.current ||
                applicantStageMutationGenerations.current,
            `${JobData.JobId}:${normalizeApplicantId(applicantId)}`
        );
    const fnAfterUpdate = (
        params: Parameters<typeof refreshApplicantStageView>[0]["params"] & {
            stageChangeShouldApply?: () => boolean;
        }
    ) => {
        const stageChangeShouldApply =
            params.stageChangeShouldApply || (() => true);
        if (!stageChangeShouldApply()) {
            return Promise.resolve();
        }
        return refreshApplicantStageView({
            hasJobContext,
            refreshKanban: updateAllInfo,
            refreshApplicant: async () => {
                if (!stageChangeShouldApply()) {
                    return;
                }
                const confirmedStage = stages.find(
                    (stage) => stage.id === params.newStageId
                );
                if (confirmedStage) {
                    refreshApplicantProfileAfterStageChange({
                        queryClient,
                        queryKey: data.applicantDataReturn.useQueryKey,
                        jobId: JobData.JobId,
                        requestId: params.requestId,
                        stage: confirmedStage
                    });
                } else {
                    void queryClient.cancelQueries(
                        data.applicantDataReturn.useQueryKey,
                        { exact: true },
                        { revert: false }
                    );
                    void queryClient.invalidateQueries(
                        data.applicantDataReturn.useQueryKey,
                        {
                            exact: true,
                            refetchActive: false
                        }
                    );
                }
            },
            invalidateKanban: () => {
                for (const keyName of keysToInvalidate) {
                    queryClient.invalidateQueries(keyName);
                }
            },
            params,
            shouldContinue: stageChangeShouldApply
        });
    };
    const postAddressSearch = `/job/search`;
    const [getJobSearchResult, getJobSearch] = useApiCache(
        postAddressSearch,
        "post",
        { id: JobData.JobId, companyId: data.companyId, searchForEdit: true },
        false,
        1000 * 60 * 5,
        false
    );

    React.useEffect(() => {
        if (getJobSearchResult.status === "success" && !stages.length) {
            const dbStages =
                getJobSearchResult.response?.data?.mainData?.pipeline?.stages ||
                [];
            const stagesToUpdate: IJobViewStages[] = dbStages.map(
                (item: any) => {
                    const actionsStage: IJobViewStagesActionDefinition = {};
                    for (const actionName of pipelineAllActionsFields) {
                        if (item[actionName]) {
                            actionsStage[actionName] = item[actionName];
                        }
                    }
                    const obj: IJobViewStages = {
                        id: item.id,
                        name: item.name,
                        fixed: item.fixed,
                        order: item.order,
                        type: item?.type?.value || "",
                        maxDaysInStage: item?.maxDaysInStage,
                        maxItensKanban: item?.maxItensKanban
                    };
                    if (Object.keys(actionsStage).length) {
                        obj.actions = actionsStage;
                    }

                    return obj;
                }
            );
            stagesToUpdate.sort((a, b) => a.order - b.order);
            setStages(stagesToUpdate);
        }
    }, [getJobSearchResult.status]);

    const ApplicantData = {
        ...data.applicantData,
        jobData: JobData
    };
    const stage =
        JobData.Stage ||
        stages.filter((stage: any) => stage.id === JobData.PipelineStage)[0];

    const readOnly = JobData.status !== "open";

    if (stages.length) {
        return (
            <ChangeStageButton
                applicantData={ApplicantData}
                currentStage={stage}
                stages={stages}
                jobData={JobData}
                language={language}
                listKeyToInvalidate={keysToInvalidate}
                fnBeforeUpdate={startJobStageChange}
                fnAfterUpdate={fnAfterUpdate}
                currentIndex={index}
                outsideKanban={true}
                fontSize={13}
                showAsTag={true}
                t={t}
                readonly={readOnly}
            />
        );
    } else {
        return null;
    }
};

export default ChangeStageButtonOutsideJobContainer;
